import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings as SettingsIcon } from "lucide-react";

/**
 * GDPR-compliant cookie consent banner.
 * - Required for EU users and to be AdSense-compliant.
 * - Stores choice in localStorage as "bdd_consent" with payload {ads, analytics, ts}.
 * - Components that depend on consent (AdSlot, Analytics) should read this key.
 */
const KEY = "bdd_consent";

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(KEY);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

export function setConsent(payload) {
  const v = { ...payload, ts: Date.now() };
  localStorage.setItem(KEY, JSON.stringify(v));
  // notify same-tab listeners
  window.dispatchEvent(new Event("bdd_consent_changed"));
}

export default function CookieConsent() {
  // initial open state computed from stored consent (no useEffect needed)
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return !getConsent();
  });
  const [showPrefs, setShowPrefs] = useState(false);
  const [ads, setAds] = useState(true);
  const [analytics, setAnalytics] = useState(true);

  const acceptAll = () => {
    setConsent({ ads: true, analytics: true });
    setOpen(false);
  };
  const rejectAll = () => {
    setConsent({ ads: false, analytics: false });
    setOpen(false);
  };
  const savePrefs = () => {
    setConsent({ ads, analytics });
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="cookie-consent"
      className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6 slide-up"
    >
      <div className="max-w-5xl mx-auto bg-[#1c2733]/95 backdrop-blur-xl border border-[#2e3c4d] rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 sm:p-6 flex flex-col md:flex-row gap-5 items-start">
          <div className="hidden md:flex w-12 h-12 rounded-xl bg-[#FBD51C]/15 items-center justify-center shrink-0">
            <Cookie size={22} className="text-[#FBD51C]" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg text-white mb-1">
              Usiamo i cookie 🍪
            </h2>
            <p className="text-sm text-[#a8b9cc] leading-relaxed">
              Utilizziamo cookie tecnici per il funzionamento del sito e, con il
              tuo consenso, cookie di Google AdSense per mostrarti annunci
              contestuali e di Analytics per migliorare il servizio.{" "}
              <Link
                to="/cookie"
                className="text-[#FBD51C] hover:underline whitespace-nowrap"
              >
                Maggiori info
              </Link>
              .
            </p>

            {showPrefs && (
              <div
                data-testid="cookie-prefs"
                className="mt-4 space-y-3 border-t border-[#2e3c4d] pt-4"
              >
                <PrefRow
                  title="Cookie tecnici"
                  desc="Necessari per il funzionamento del sito (sessione, preferenze). Sempre attivi."
                  checked
                  disabled
                />
                <PrefRow
                  title="Pubblicitari (AdSense)"
                  desc="Permettono di mostrare annunci personalizzati di Google AdSense."
                  checked={ads}
                  onChange={setAds}
                  testid="pref-ads"
                />
                <PrefRow
                  title="Analytics"
                  desc="Statistiche di traffico anonime per migliorare il sito."
                  checked={analytics}
                  onChange={setAnalytics}
                  testid="pref-analytics"
                />
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-3 items-center">
              <button
                data-testid="cookie-reject"
                onClick={rejectAll}
                className="text-sm font-bold text-[#a8b9cc] hover:text-white px-4 py-2.5 rounded-lg border border-[#2e3c4d] hover:border-[#3d4d63] bg-transparent transition-colors"
              >
                Rifiuta tutti
              </button>

              {!showPrefs ? (
                <button
                  data-testid="cookie-customize"
                  onClick={() => setShowPrefs(true)}
                  className="text-sm font-bold text-[#a8b9cc] hover:text-white px-4 py-2.5 rounded-lg border border-[#2e3c4d] hover:border-[#3d4d63] bg-transparent inline-flex items-center gap-2 transition-colors"
                >
                  <SettingsIcon size={14} /> Personalizza
                </button>
              ) : (
                <button
                  data-testid="cookie-save"
                  onClick={savePrefs}
                  className="text-sm font-bold text-white px-4 py-2.5 rounded-lg border border-[#2e3c4d] hover:bg-[#243447] bg-transparent transition-colors"
                >
                  Salva preferenze
                </button>
              )}

              <button
                data-testid="cookie-accept"
                onClick={acceptAll}
                className="text-sm font-black text-black px-5 py-2.5 rounded-lg bg-[#FBD51C] hover:bg-[#FFE552] brand-cta-glow transition-all ml-auto"
              >
                Accetta tutti
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <label
      className={`flex items-start gap-3 ${disabled ? "opacity-70" : "cursor-pointer"}`}
    >
      <input
        data-testid={testid}
        type="checkbox"
        className="mt-1 accent-[#FBD51C]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
      />
      <div>
        <div className="text-sm font-bold text-white">{title}</div>
        <div className="text-xs text-[#a8b9cc]">{desc}</div>
      </div>
    </label>
  );
}
